import React, { useEffect, useState } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import Select, { SingleValue } from "react-select";
import { Row, Col, Button, Spinner } from "reactstrap";
import { Drawer, message } from "antd";
import rolesApi from "../../../api/master/roles";

interface Employee {
  name: string;
  id: string;
}

interface Role {
  roleName: string;
  employees: Employee[];
  roleID: any;
  level: string;
}

interface CustomDrawerProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: ApprovalData[]) => void;
  setApprovalSubmissionStatus: (status: boolean) => void;
  approvalData: ApprovalData[];
  siteId: number;
}

interface FormValues {
  [key: string]: string;
}

interface ApprovalData {
  roleId: any;
  level: string;
  userId: any;
  approvalStage?: string;
  roleName?: string;
  userName?: string;
}

const WorkRequestApprovalDrawer: React.FC<CustomDrawerProps> = ({
  open,
  onClose,
  onSubmit,
  setApprovalSubmissionStatus,
  approvalData,
  siteId,
}) => {
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<FormValues>();
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [alertInfo, setAlertInfo] = useState<any>(null);

  // Approval stage options
  const approvalStageOptions = [
    { value: "Verifier", label: "Verifier" },
    { value: "Checker", label: "Checker" },
    { value: "Approver", label: "Approver" },
  ];

  const fetchApprovalHierarchyUsersByModuleId = async (
    moduleID: number,
    siteId: number
  ) => {
    try {
      const res = await rolesApi.fetchHierarchyUsersByModuleId(
        moduleID,
        siteId
      );
      if (!res) return;
      const { data, status, message } = res;
      if (status) {
        setRoles(data);
        setLoading(false);
      } else {
        setAlertInfo(message);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchModuleConfig = async () => {
    try {
      const res = await rolesApi.findAllModuleConfig();
      const moduleID = res?.data?.find(
        (x: any) => x.moduleName == "Work Request"
      )?.moduleID || 126;
      await fetchApprovalHierarchyUsersByModuleId(moduleID, siteId);
    } catch (err) {
      throw err;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchModuleConfig();
  }, []);

  useEffect(() => {
    roles.forEach((role, index) => {
      register(role.level, { required: "This field is required" });
      register(`${role.level}_approvalStage`, {
        required: "Approval stage is required",
      });
    });
  }, [roles, register]);

  useEffect(() => {
    if (approvalData && approvalData.length > 0) {
      approvalData.forEach((data, index) => {
        setValue(data.level, data.userId);
        if (data.approvalStage) {
          setValue(`${data.level}_approvalStage`, data.approvalStage);
        }
      });
      setLoading(false);
    }
  }, [approvalData, setValue]);

  const submitApprovals: SubmitHandler<FormValues> = (data) => {
    const approvalData: ApprovalData[] = roles.map((role, index) => {
      // Find the selected employee to get their name
      const selectedUserId = data[`${role.level}`];
      const selectedEmployee = role.employees?.find(
        (emp) => emp.id === selectedUserId
      );

      return {
        roleId: Number(role.roleID),
        level: role.level,
        userId: selectedUserId,
        approvalStage: data[`${role.level}_approvalStage`],
        roleName: role.roleName,
        userName: selectedEmployee?.name || "",
      };
    });

    // Validation: Check if at least one Approver exists
    const hasApprover = approvalData.some(
      (approval) => approval.approvalStage === "Approver"
    );
    if (!hasApprover) {
      message.error(
        "At least one Approver must be configured in the hierarchy"
      );
      return;
    }

    onSubmit(approvalData);
    setApprovalSubmissionStatus(true);
  };

  const handleSelectChange = (
    name: string,
    value: SingleValue<{ value: string; label: string }>
  ) => {
    if (value && value.value) {
      setValue(name, value.value, { shouldValidate: true });
    }
  };

  const getDefaultValue = (role: Role) => {
    const approval = approvalData?.find((data) => data.level === role.level);
    if (approval) {
      const employee = role.employees?.find(
        (emp) => emp.id === approval.userId
      );
      if (employee) {
        return { value: employee.id, label: employee.name };
      }
    }
    return null;
  };

  const getDefaultApprovalStage = (role: Role) => {
    const approval = approvalData?.find((data) => data.level === role.level);
    if (approval && approval.approvalStage) {
      return approvalStageOptions.find(
        (option) => option.value === approval.approvalStage
      );
    }
    return null;
  };

  return (
    <Drawer
      title="Set Work Order Approvals"
      placement="right"
      onClose={onClose}
      open={open}
      width={600}
    >
      {loading ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
          }}
        >
          <Spinner color="primary" />
        </div>
      ) : (
        <div>
          {alertInfo ? (
            <p className="text-center">{alertInfo}</p>
          ) : (
            <>
              <div style={{ marginBottom: "20px" }}>
                <p>
                  There are {roles.length} levels of approvals set for this work
                  order process
                </p>
                <div
                  style={{
                    backgroundColor: "#fff7e6",
                    border: "1px solid #ffd591",
                    borderRadius: "4px",
                    padding: "10px",
                    marginTop: "10px",
                  }}
                >
                  <strong>Note:</strong> At least one user must be assigned as
                  an <strong>Approver</strong> in the hierarchy.
                </div>
              </div>
              <form onSubmit={handleSubmit(submitApprovals)}>
                {roles.map((role, index) => (
                  <Row
                    className="mt-3"
                    key={index}
                    style={{
                      border: "1px solid #e8e8e8",
                      padding: "15px",
                      borderRadius: "5px",
                      marginBottom: "10px",
                    }}
                  >
                    <Col lg="12">
                      <h6
                        style={{ marginBottom: "15px", color: "#1890ff" }}
                      >{`${role.roleName}`}</h6>
                    </Col>
                    <Col lg="6">
                      <label
                        style={{
                          fontWeight: "bold",
                          marginBottom: "8px",
                          display: "block",
                        }}
                      >
                        Select User
                      </label>
                      <Select
                        classNamePrefix="react-select"
                        options={role.employees?.map((employee) => ({
                          value: employee.id,
                          label: employee.name,
                        }))}
                        onChange={(selectedOption) =>
                          handleSelectChange(`${role.level}`, selectedOption)
                        }
                        placeholder={`Select ${role.roleName}`}
                        defaultValue={getDefaultValue(role)}
                      />
                      {errors[`${role.level}`] && (
                        <p className="mandatory">
                          {errors[`${role.level}`]?.message}
                        </p>
                      )}
                    </Col>
                    <Col lg="6">
                      <label
                        style={{
                          fontWeight: "bold",
                          marginBottom: "8px",
                          display: "block",
                        }}
                      >
                        Approval Stage
                      </label>
                      <Select
                        classNamePrefix="react-select"
                        options={approvalStageOptions}
                        onChange={(selectedOption) =>
                          handleSelectChange(
                            `${role.level}_approvalStage`,
                            selectedOption
                          )
                        }
                        placeholder="Select Stage"
                        defaultValue={getDefaultApprovalStage(role)}
                      />
                      {errors[`${role.level}_approvalStage`] && (
                        <p className="mandatory">
                          {errors[`${role.level}_approvalStage`]?.message}
                        </p>
                      )}
                    </Col>
                  </Row>
                ))}
                <Button type="submit" color="primary" className="mt-3">
                  Save
                </Button>
              </form>
            </>
          )}
        </div>
      )}
    </Drawer>
  );
};

export default WorkRequestApprovalDrawer;
